import './styles.css';

type Mode = 'daily' | 'quick' | 'local' | 'challenge';
type Screen = 'menu' | 'ready' | 'playing' | 'handoff' | 'result';
type Hit = 'perfect' | 'good' | 'miss';
type RunResult = { score: number; perfect: number; good: number; miss: number; maxCombo: number; seed: number; bloom: Petal[] };
type Challenge = { v: 1; seed: number; score: number; accuracy: number; name: string };
type Petal = { angle: number; length: number; width: number; hue: number; ring: number; perfect: boolean };

const app = document.querySelector<HTMLDivElement>('#app')!;
const RUN_MS = 45_000;
const TAU = Math.PI * 2;
const palette = { ink: '#f9f1d5', muted: '#b9c8b6', green: '#10251d', leaf: '#52d68b', sun: '#f7ce69', coral: '#ff6b55', sky: '#83d7d0' };

let mode: Mode = 'daily';
let screen: Screen = 'menu';
let seed = dailySeed();
let random = rng(seed);
let visualRandom = rng(seed ^ 0xB1006D);
let angle = 0;
let target = 1.2;
let targetWidth = .52;
let direction = 1;
let speed = 1.75;
let score = 0;
let combo = 0;
let maxCombo = 0;
let perfect = 0;
let good = 0;
let miss = 0;
let petals: Petal[] = [];
let particles: Particle[] = [];
let startedAt = 0;
let lastFrame = 0;
let feverUntil = 0;
let feedback: Hit | '' = '';
let feedbackUntil = 0;
let shake = 0;
let raf = 0;
let player = 1;
let firstResult: RunResult | null = null;
let incoming = readChallenge();
let challengeName = localStorage.getItem('syncbloom-name') || 'Friend';
let audio: AudioContext | null = null;

type Particle = { x: number; y: number; vx: number; vy: number; life: number; color: string; size: number };

function rng(initial: number) {
  let value = initial >>> 0;
  return () => {
    value += 0x6D2B79F5;
    let t = value;
    t = Math.imul(t ^ t >>> 15, t | 1);
    t ^= t + Math.imul(t ^ t >>> 7, t | 61);
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  };
}

function hashText(text: string) {
  let h = 2166136261;
  for (let i = 0; i < text.length; i++) h = Math.imul(h ^ text.charCodeAt(i), 16777619);
  return h >>> 0;
}

function dailySeed() {
  const day = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Singapore', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
  return hashText(`syncbloom-${day}`);
}

function safeName(value: string) {
  return value.replace(/[<>\u0000-\u001f]/g, '').trim().slice(0, 18) || 'Friend';
}

function b64Encode(value: object) {
  const bytes = new TextEncoder().encode(JSON.stringify(value));
  let binary = '';
  bytes.forEach(byte => binary += String.fromCharCode(byte));
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function b64Decode<T>(value: string): T | null {
  try {
    const raw = value.replace(/-/g, '+').replace(/_/g, '/');
    const binary = atob(raw + '='.repeat((4 - raw.length % 4) % 4));
    const bytes = Uint8Array.from(binary, char => char.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes)) as T;
  } catch { return null; }
}

function readChallenge(): Challenge | null {
  const token = new URLSearchParams(location.search).get('c');
  const parsed = token ? b64Decode<Challenge>(token) : null;
  if (!parsed || parsed.v !== 1 || !Number.isFinite(parsed.seed) || !Number.isFinite(parsed.score)) return null;
  return { ...parsed, name: safeName(parsed.name) };
}

function accuracy(result: Pick<RunResult, 'perfect' | 'good' | 'miss'>) {
  const total = result.perfect + result.good + result.miss;
  return total ? Math.round(((result.perfect + result.good * .65) / total) * 100) : 0;
}

function titleFor(result: RunResult) {
  const acc = accuracy(result);
  if (result.maxCombo >= 20 && acc >= 90) return 'Solar Virtuoso';
  if (result.maxCombo >= 12) return 'Petal Architect';
  if (acc >= 85) return 'Quiet Precision';
  if (result.score >= 4500) return 'Wild Gardener';
  return 'First Light';
}

function modeLabel() {
  if (mode === 'daily') return 'Daily Bloom';
  if (mode === 'quick') return 'Quick Bloom';
  if (mode === 'local') return `Local Duel · Player ${player}`;
  return incoming ? `Challenge from ${incoming.name}` : 'Friend Challenge';
}

function render() {
  cancelAnimationFrame(raf);
  if (screen === 'menu') renderMenu();
  else if (screen === 'ready') renderReady();
  else if (screen === 'handoff') renderHandoff();
  else if (screen === 'result') renderResult();
  else renderGame();
}

function logo() {
  return '<div class="wordmark"><span class="seed-mark" aria-hidden="true"></span><span>SYNCBLOOM</span></div>';
}

function renderMenu() {
  const challenge = incoming ? `<section class="incoming"><p>${incoming.name} grew a <b>${incoming.score.toLocaleString()}</b> bloom.</p><button class="primary" data-mode="challenge">Play the same run</button></section>` : '';
  app.innerHTML = `<main class="menu-shell">
    <header>${logo()}<a class="library-link" href="/">All games</a></header>
    <section class="hero"><div class="hero-copy"><p class="kicker">One thumb. One orbit. One bloom.</p><h1>Grow it.<br>Share it.<br><em>Beat it.</em></h1><p>Catch the pulse inside the light. Perfect taps grow sharper petals. Misses leave a mark. Every 45-second run becomes a flower only you could make.</p></div><canvas id="menu-bloom" aria-hidden="true"></canvas></section>
    ${challenge}
    <section class="mode-list" aria-label="Game modes">
      <button class="mode" data-mode="daily"><span><b>Daily Bloom</b><small>One shared seed every day</small></span><i>Solo</i></button>
      <button class="mode" data-mode="quick"><span><b>Quick Bloom</b><small>Fresh run, instant restart</small></span><i>Solo</i></button>
      <button class="mode" data-mode="local"><span><b>Same-phone Duel</b><small>Two players, identical orbit</small></span><i>2P</i></button>
    </section>
    <p class="menu-note">Sound and haptics switch on after your first tap. Best played upright.</p>
  </main>`;
  app.querySelectorAll<HTMLElement>('[data-mode]').forEach(button => button.addEventListener('click', () => selectMode(button.dataset.mode as Mode)));
  const canvas = document.querySelector<HTMLCanvasElement>('#menu-bloom')!;
  const demo = Array.from({ length: 28 }, (_, i) => ({ angle: i * TAU / 14 + (i % 2) * .12, length: 56 + (i % 5) * 7, width: .18, hue: 42 + (i % 4) * 18, ring: i % 2, perfect: i % 3 !== 0 }));
  drawStaticBloom(canvas, demo, 0xB1006D);
}

function selectMode(next: Mode) {
  mode = next;
  player = 1;
  firstResult = null;
  if (mode === 'daily') seed = dailySeed();
  else if (mode === 'challenge' && incoming) seed = incoming.seed;
  else seed = crypto.getRandomValues(new Uint32Array(1))[0];
  screen = 'ready';
  render();
}

function renderReady() {
  const opponent = mode === 'challenge' && incoming ? `<p class="target-score">Target <b>${incoming.score.toLocaleString()}</b> · ${incoming.accuracy}% accuracy</p>` : '';
  app.innerHTML = `<main class="ready-shell"><header>${logo()}<button class="text-button" data-action="menu">Exit</button></header><section class="ready-card"><div class="orb-preview"><span></span><i></i></div><p class="kicker">${modeLabel()}</p><h1>Tap inside<br>the light.</h1>${opponent}<ol><li><b>Perfect</b> taps grow long petals.</li><li>Five Perfects start <b>Fever</b>.</li><li>Three misses end the bloom early.</li></ol><button class="primary start" data-action="start">Start 45-second run</button></section></main>`;
  bindActions();
}

function resetRun() {
  random = rng(seed);
  visualRandom = rng(seed ^ 0xB1006D);
  angle = random() * TAU;
  target = random() * TAU;
  targetWidth = .58;
  direction = random() > .5 ? 1 : -1;
  speed = 1.72;
  score = combo = maxCombo = perfect = good = miss = 0;
  petals = [];
  particles = [];
  feedback = '';
  feverUntil = 0;
  startedAt = performance.now();
  lastFrame = startedAt;
}

function beginRun() {
  initAudio();
  resetRun();
  screen = 'playing';
  render();
}

function renderGame() {
  app.innerHTML = `<main class="game-shell"><header class="game-hud"><div><span>${modeLabel()}</span><b id="score">0</b></div><div class="time-wrap"><span id="time">45.0</span><i id="time-bar"></i></div><div class="lives" id="lives" aria-label="Three chances"><i></i><i></i><i></i></div></header><canvas id="game" aria-label="SYNCBLOOM play field"></canvas><div class="feedback" id="feedback"></div><div class="combo" id="combo"></div><button class="tap-zone" id="tap-zone" aria-label="Tap to catch the pulse"><span>Tap anywhere</span></button></main>`;
  document.getElementById('tap-zone')!.addEventListener('pointerdown', event => { event.preventDefault(); tap(); });
  document.addEventListener('keydown', keyTap);
  raf = requestAnimationFrame(frame);
}

function keyTap(event: KeyboardEvent) {
  if (screen === 'playing' && (event.code === 'Space' || event.code === 'Enter')) { event.preventDefault(); tap(); }
}

function angularDistance(a: number, b: number) {
  return Math.abs(Math.atan2(Math.sin(a - b), Math.cos(a - b)));
}

function tap() {
  const now = performance.now();
  const fever = now < feverUntil;
  const distance = angularDistance(angle, target);
  const window = targetWidth * (fever ? 1.28 : 1);
  let hit: Hit;
  if (distance <= window * .32) hit = 'perfect';
  else if (distance <= window) hit = 'good';
  else hit = 'miss';
  feedback = hit;
  feedbackUntil = now + 430;
  if (hit === 'perfect') {
    perfect++; combo++; maxCombo = Math.max(maxCombo, combo);
    score += Math.round((110 + combo * 13) * (fever ? 2 : 1));
    addPetal(true); burst(palette.sun, 20); tone(620 + Math.min(combo, 18) * 22, .07, 'sine');
    if (combo > 0 && combo % 5 === 0) { feverUntil = now + 5200; tone(880, .16, 'triangle'); haptic([20, 30, 45]); }
    else haptic(12);
  } else if (hit === 'good') {
    good++; combo++; maxCombo = Math.max(maxCombo, combo);
    score += Math.round((55 + combo * 7) * (fever ? 2 : 1));
    addPetal(false); burst(palette.sky, 10); tone(440, .06, 'sine'); haptic(8);
  } else {
    miss++; combo = 0; shake = 7;
    burst(palette.coral, 12); tone(145, .1, 'sawtooth'); haptic([35, 25, 35]);
  }
  nextTarget();
  updateHud();
  if (miss >= 3) finishRun();
}

function nextTarget() {
  const elapsed = Math.min(1, (performance.now() - startedAt) / RUN_MS);
  const leap = .75 + random() * 3.2;
  target = (target + direction * leap + TAU) % TAU;
  if (random() < .16 + elapsed * .20) direction *= -1;
  speed = 1.75 + elapsed * 1.75;
  targetWidth = Math.max(.28, .58 - elapsed * .20);
}

function addPetal(isPerfect: boolean) {
  const index = petals.length;
  petals.push({
    angle: target + (visualRandom() - .5) * .09,
    length: isPerfect ? 68 + Math.min(combo, 20) * 2 + visualRandom() * 15 : 48 + visualRandom() * 18,
    width: isPerfect ? .17 + visualRandom() * .04 : .22 + visualRandom() * .06,
    hue: 35 + ((index * 17 + seed % 90) % 105),
    ring: Math.floor(index / 12),
    perfect: isPerfect
  });
}

function frame(now: number) {
  if (screen !== 'playing') return;
  const dt = Math.min(.034, (now - lastFrame) / 1000);
  lastFrame = now;
  angle = (angle + dt * speed * direction + TAU) % TAU;
  particles.forEach(p => { p.x += p.vx * dt; p.y += p.vy * dt; p.vy += 55 * dt; p.life -= dt; });
  particles = particles.filter(p => p.life > 0);
  shake *= .84;
  drawGame(now);
  updateHud(now);
  if (now - startedAt >= RUN_MS) finishRun();
  else raf = requestAnimationFrame(frame);
}

function canvasMetrics(canvas: HTMLCanvasElement) {
  const dpr = Math.min(devicePixelRatio || 1, 2);
  const rect = canvas.getBoundingClientRect();
  const width = Math.max(1, rect.width);
  const height = Math.max(1, rect.height);
  if (canvas.width !== Math.round(width * dpr) || canvas.height !== Math.round(height * dpr)) {
    canvas.width = Math.round(width * dpr); canvas.height = Math.round(height * dpr);
  }
  const ctx = canvas.getContext('2d')!;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  return { ctx, width, height };
}

function drawGame(now: number) {
  const canvas = document.querySelector<HTMLCanvasElement>('#game');
  if (!canvas) return;
  const { ctx, width, height } = canvasMetrics(canvas);
  ctx.clearRect(0, 0, width, height);
  const cx = width / 2 + (randomVisual(now) - .5) * shake;
  const cy = height * .47 + (randomVisual(now + 8) - .5) * shake;
  const radius = Math.min(width * .34, height * .255, 150);
  drawBloom(ctx, cx, cy, petals, radius * .52, now);
  ctx.save(); ctx.translate(cx, cy);
  ctx.strokeStyle = 'rgba(249,241,213,.17)'; ctx.lineWidth = 2; ctx.beginPath(); ctx.arc(0, 0, radius, 0, TAU); ctx.stroke();
  const fever = now < feverUntil;
  ctx.strokeStyle = fever ? palette.sun : palette.leaf; ctx.lineWidth = fever ? 17 : 13; ctx.lineCap = 'round'; ctx.shadowColor = ctx.strokeStyle; ctx.shadowBlur = fever ? 25 : 14;
  ctx.beginPath(); ctx.arc(0, 0, radius, target - targetWidth, target + targetWidth); ctx.stroke(); ctx.shadowBlur = 0;
  const sx = Math.cos(angle) * radius, sy = Math.sin(angle) * radius;
  ctx.fillStyle = palette.ink; ctx.shadowColor = fever ? palette.sun : palette.ink; ctx.shadowBlur = 18; ctx.beginPath(); ctx.arc(sx, sy, fever ? 11 : 9, 0, TAU); ctx.fill(); ctx.shadowBlur = 0;
  ctx.fillStyle = fever ? palette.sun : palette.coral; ctx.beginPath(); ctx.arc(sx, sy, 4, 0, TAU); ctx.fill();
  for (const p of particles) { ctx.globalAlpha = Math.max(0, p.life / .7); ctx.fillStyle = p.color; ctx.beginPath(); ctx.arc(p.x, p.y, p.size, 0, TAU); ctx.fill(); }
  ctx.restore(); ctx.globalAlpha = 1;
  if (fever) { ctx.fillStyle = palette.sun; ctx.font = '800 12px Avenir Next, sans-serif'; ctx.textAlign = 'center'; ctx.fillText('FEVER · 2×', cx, cy + radius + 42); }
}

function randomVisual(value: number) { return (Math.sin(value * 12.9898) * 43758.5453) % 1 + 1 % 1; }

function drawBloom(ctx: CanvasRenderingContext2D, cx: number, cy: number, bloom: Petal[], base: number, now = 0) {
  ctx.save(); ctx.translate(cx, cy);
  bloom.forEach((petal, index) => {
    const appear = Math.min(1, Math.max(0, (now - startedAt - index * 25) / 220));
    const inner = base * .35 + petal.ring * 5;
    const len = Math.min(base * 1.8, petal.length) * (.84 + appear * .16);
    ctx.save(); ctx.rotate(petal.angle); ctx.translate(inner, 0); ctx.scale(appear || 1, appear || 1);
    ctx.fillStyle = `hsl(${petal.hue} 78% ${petal.perfect ? 63 : 70}% / .94)`;
    ctx.strokeStyle = 'rgba(16,37,29,.68)'; ctx.lineWidth = 1.4;
    ctx.beginPath(); ctx.moveTo(0, 0); ctx.bezierCurveTo(len * .25, -len * petal.width, len * .78, -len * petal.width * .72, len, 0); ctx.bezierCurveTo(len * .78, len * petal.width * .72, len * .25, len * petal.width, 0, 0); ctx.closePath(); ctx.fill(); ctx.stroke();
    if (petal.perfect) { ctx.strokeStyle = 'rgba(255,255,230,.45)'; ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(len * .15, 0); ctx.lineTo(len * .78, 0); ctx.stroke(); }
    ctx.restore();
  });
  ctx.fillStyle = palette.sun; ctx.strokeStyle = palette.green; ctx.lineWidth = 3; ctx.beginPath(); ctx.arc(0, 0, base * .42, 0, TAU); ctx.fill(); ctx.stroke();
  ctx.fillStyle = palette.coral; ctx.beginPath(); ctx.arc(0, 0, base * .18, 0, TAU); ctx.fill(); ctx.restore();
}

function burst(color: string, count: number) {
  const angleNow = angle;
  for (let i = 0; i < count; i++) {
    const a = angleNow + (visualRandom() - .5) * 1.2, force = 30 + visualRandom() * 90;
    particles.push({ x: Math.cos(angleNow) * 110, y: Math.sin(angleNow) * 110, vx: Math.cos(a) * force, vy: Math.sin(a) * force, life: .45 + visualRandom() * .35, color, size: 1.5 + visualRandom() * 3 });
  }
}

function updateHud(now = performance.now()) {
  const scoreEl = document.getElementById('score'), timeEl = document.getElementById('time'), bar = document.getElementById('time-bar');
  if (!scoreEl || !timeEl || !bar) return;
  const remaining = Math.max(0, RUN_MS - (now - startedAt));
  scoreEl.textContent = score.toLocaleString(); timeEl.textContent = (remaining / 1000).toFixed(1); bar.style.transform = `scaleX(${remaining / RUN_MS})`;
  document.querySelectorAll('.lives i').forEach((life, index) => life.classList.toggle('lost', index >= 3 - miss));
  const feedbackEl = document.getElementById('feedback')!, comboEl = document.getElementById('combo')!;
  feedbackEl.textContent = now < feedbackUntil ? feedback.toUpperCase() : '';
  feedbackEl.className = `feedback ${now < feedbackUntil ? feedback : ''}`;
  comboEl.textContent = combo >= 2 ? `${combo} chain` : '';
}

function currentResult(): RunResult { return { score, perfect, good, miss, maxCombo, seed, bloom: [...petals] }; }

function finishRun() {
  if (screen !== 'playing') return;
  cancelAnimationFrame(raf); document.removeEventListener('keydown', keyTap);
  const result = currentResult();
  if (mode === 'local' && player === 1) { firstResult = result; player = 2; screen = 'handoff'; }
  else { firstResult ??= mode === 'challenge' && incoming ? { score: incoming.score, perfect: incoming.accuracy, good: 0, miss: 0, maxCombo: 0, seed: incoming.seed, bloom: [] } : null; screen = 'result'; }
  saveBest(result); render();
}

function saveBest(result: RunResult) {
  const key = mode === 'daily' ? `syncbloom-daily-${seed}` : 'syncbloom-best';
  const old = Number(localStorage.getItem(key) || 0);
  if (result.score > old) localStorage.setItem(key, String(result.score));
}

function renderHandoff() {
  app.innerHTML = `<main class="handoff-shell"><section><p class="kicker">Player 1 complete</p><h1>${firstResult!.score.toLocaleString()}</h1><p>Pass the phone to Player 2. The orbit sequence will be identical.</p><button class="primary" data-action="start">Player 2 ready</button></section></main>`;
  bindActions();
}

function renderResult() {
  const result = currentResult();
  const acc = accuracy(result);
  const rivalScore = mode === 'local' ? firstResult?.score : incoming?.score;
  const won = rivalScore == null ? null : result.score > rivalScore;
  const outcome = won == null ? '' : `<p class="outcome ${won ? 'win' : 'loss'}">${won ? 'You won the bloom.' : result.score === rivalScore ? 'Perfect tie.' : 'The other bloom wins.'}</p>`;
  const best = Number(localStorage.getItem(mode === 'daily' ? `syncbloom-daily-${seed}` : 'syncbloom-best') || result.score);
  app.innerHTML = `<main class="result-shell"><header>${logo()}<a class="library-link" href="/">All games</a></header><section class="result-art"><canvas id="result-bloom" aria-label="Your generated bloom"></canvas></section><section class="result-copy"><p class="kicker">${titleFor(result)}</p><h1>${result.score.toLocaleString()}</h1>${outcome}<div class="result-stats"><span><b>${acc}%</b> accuracy</span><span><b>${result.maxCombo}</b> best chain</span><span><b>${best.toLocaleString()}</b> local best</span></div><div class="name-row"><label>Your challenge name<input id="player-name" maxlength="18" value="${safeName(challengeName)}" /></label></div><div class="result-actions"><button class="primary" data-action="share">Challenge a friend</button><button data-action="again">Run it again</button><button data-action="menu">Choose mode</button></div><p class="share-status" id="share-status" role="status"></p></section></main>`;
  drawStaticBloom(document.querySelector<HTMLCanvasElement>('#result-bloom')!, result.bloom, seed);
  bindActions();
}

function drawStaticBloom(canvas: HTMLCanvasElement, bloom: Petal[], localSeed: number) {
  const dpr = Math.min(devicePixelRatio || 1, 2), size = Math.max(280, Math.min(innerWidth - 32, 500));
  canvas.width = size * dpr; canvas.height = size * dpr; canvas.style.width = `${size}px`; canvas.style.height = `${size}px`;
  const ctx = canvas.getContext('2d')!; ctx.scale(dpr, dpr);
  ctx.fillStyle = palette.green; ctx.fillRect(0, 0, size, size);
  const glow = ctx.createRadialGradient(size / 2, size / 2, 8, size / 2, size / 2, size * .48); glow.addColorStop(0, 'rgba(247,206,105,.14)'); glow.addColorStop(1, 'rgba(16,37,29,0)'); ctx.fillStyle = glow; ctx.fillRect(0, 0, size, size);
  drawBloom(ctx, size / 2, size / 2, bloom, Math.min(65, size * .14));
  ctx.fillStyle = 'rgba(249,241,213,.38)'; ctx.font = '700 11px Avenir Next, sans-serif'; ctx.textAlign = 'center'; ctx.fillText(`SEED ${localSeed.toString(36).toUpperCase()}`, size / 2, size - 22);
}

function bindActions() {
  app.querySelectorAll<HTMLElement>('[data-action]').forEach(button => button.addEventListener('click', async () => {
    const action = button.dataset.action;
    if (action === 'start') beginRun();
    if (action === 'menu') { incoming = readChallenge(); screen = 'menu'; render(); }
    if (action === 'again') { player = 1; firstResult = null; screen = 'ready'; render(); }
    if (action === 'share') await shareChallenge();
  }));
}

async function shareChallenge() {
  const input = document.querySelector<HTMLInputElement>('#player-name'); challengeName = safeName(input?.value || challengeName); localStorage.setItem('syncbloom-name', challengeName);
  const result = currentResult();
  const payload: Challenge = { v: 1, seed, score: result.score, accuracy: accuracy(result), name: challengeName };
  const url = new URL(location.href); url.search = `?c=${b64Encode(payload)}`; url.hash = '';
  const text = `${challengeName} grew a ${result.score.toLocaleString()}-point bloom with ${payload.accuracy}% accuracy. Can you beat it?`;
  const status = document.getElementById('share-status')!;
  try {
    const file = await shareImage(result);
    if (navigator.share) {
      const data: ShareData = { title: 'SYNCBLOOM challenge', text, url: url.toString() };
      if (file && navigator.canShare?.({ files: [file] })) data.files = [file];
      await navigator.share(data); status.textContent = 'Challenge ready.';
    } else { await navigator.clipboard.writeText(`${text}\n${url}`); status.textContent = 'Challenge link copied.'; }
  } catch (error) {
    if ((error as DOMException)?.name === 'AbortError') return;
    try { await navigator.clipboard.writeText(`${text}\n${url}`); status.textContent = 'Challenge link copied.'; }
    catch { status.textContent = url.toString(); }
  }
}

async function shareImage(result: RunResult) {
  const canvas = document.createElement('canvas'); canvas.width = 1080; canvas.height = 1350;
  const ctx = canvas.getContext('2d')!; ctx.fillStyle = palette.green; ctx.fillRect(0, 0, canvas.width, canvas.height);
  const glow = ctx.createRadialGradient(540, 550, 60, 540, 550, 480); glow.addColorStop(0, 'rgba(247,206,105,.16)'); glow.addColorStop(1, 'rgba(16,37,29,0)'); ctx.fillStyle = glow; ctx.fillRect(0, 0, 1080, 1100);
  drawBloom(ctx, 540, 540, result.bloom, 135);
  ctx.textAlign = 'center'; ctx.fillStyle = palette.sun; ctx.font = '700 28px Avenir Next, sans-serif'; ctx.fillText('SYNCBLOOM', 540, 1060);
  ctx.fillStyle = palette.ink; ctx.font = '900 92px Avenir Next, sans-serif'; ctx.fillText(result.score.toLocaleString(), 540, 1160);
  ctx.fillStyle = palette.muted; ctx.font = '600 30px Avenir Next, sans-serif'; ctx.fillText(`${accuracy(result)}% accuracy · ${result.maxCombo} chain · ${titleFor(result)}`, 540, 1225);
  const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/png'));
  return blob ? new File([blob], 'syncbloom-challenge.png', { type: 'image/png' }) : null;
}

function initAudio() {
  audio ??= new AudioContext();
  if (audio.state === 'suspended') void audio.resume();
}

function tone(frequency: number, duration: number, type: OscillatorType) {
  if (!audio) return;
  const oscillator = audio.createOscillator(), gain = audio.createGain(), now = audio.currentTime;
  oscillator.type = type; oscillator.frequency.setValueAtTime(frequency, now); gain.gain.setValueAtTime(.045, now); gain.gain.exponentialRampToValueAtTime(.001, now + duration);
  oscillator.connect(gain).connect(audio.destination); oscillator.start(now); oscillator.stop(now + duration);
}

function haptic(pattern: number | number[]) { if ('vibrate' in navigator) navigator.vibrate(pattern); }

render();

export { rng, hashText, b64Encode, b64Decode, accuracy, titleFor };
